import { useState, useEffect, useRef } from "react";

/* ------------------------------------------------------------------
   TWSTD PRODUCTIONS — sitio de estudio
   Paleta: negro casi puro + cian/magenta a juego con el logotipo
   (el mismo tratamiento "duotone glitch" del logo se repite en
   títulos, bordes de banner y el marcador de la marca).
------------------------------------------------------------------- */

const TOKENS = {
  bg: "#0a0a0c",
  panel: "#131316",
  panel2: "#1a1a1e",
  ink: "#f2f0ea",
  muted: "#9a9aa1",
  cyan: "#2fe0e6",
  magenta: "#ff3b6e",
  line: "rgba(242,240,234,0.12)",
};

/* ---------------- datos de ejemplo (edítalos con tu contenido) ---------------- */

const SERIES = [
  {
    id: "virtualDiva",
    title: "DreamVista 2008",
    eyebrow: "Serie original · terror digital",
    meta: "10 episodios · 2025–2026 (terror digital)",
    tagline:
      "Hay cosas que es mejor olvidar",
    synopsis:
      "Un chico explora un sistema operativo olvidado que nunca fue lanzado al público con la ayuda de la risueña asistente virtual de este; Clara",
    banners: [
      "linear-gradient(135deg, #0a0a0c 0%, #123a3c 45%, #2fe0e6 100%)",
      "linear-gradient(135deg, #0a0a0c 0%, #3a0f22 45%, #ff3b6e 100%)",
      "linear-gradient(135deg, #0a0a0c 0%, #1c1c22 40%, #4a2d3d 100%)",
    ],
  },
  {
    id: "placeholder-1",
    title: "Título de tu serie",
    eyebrow: "Serie original",
    meta: "N.º de episodios · formato",
    tagline: "Escribe aquí la frase de una línea que vende la serie.",
    synopsis:
      "Reemplaza este texto con la sinopsis real de la serie: de qué trata, quién protagoniza, y qué la hace distinta. Dos o tres frases bastan — el banner y el logotipo hacen el resto del trabajo.",
    banners: [
      "linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)",
      "linear-gradient(135deg, #0a0a0c 0%, #22181e 50%, #3a2430 100%)",
    ],
    isPlaceholder: true,
  },
  {
    id: "placeholder-2",
    title: "Otra serie en desarrollo",
    eyebrow: "Serie original",
    meta: "N.º de episodios · formato",
    tagline: "Otra línea corta que resuma el gancho.",
    synopsis:
      "Este es un espacio de ejemplo para tu siguiente producción. Sustituye el título, el texto y los degradados de banner por imágenes reales de la serie cuando estén listas.",
    banners: [
      "linear-gradient(135deg, #0a0a0c 0%, #131c22 50%, #1e3038 100%)",
      "linear-gradient(135deg, #0a0a0c 0%, #1e1418 50%, #34202a 100%)",
    ],
    isPlaceholder: true,
  },
];

const PROJECTS = [
  {
    id: "wtdv",
    title: "WTDV",
    type: "Videojuego · terror de supervivencia en primera persona",
    status: "En desarrollo",
    engine: "Unreal Engine 5",
    desc:
      "Un survival horror construido sobre un mecanismo de mundo dual: dos versiones espejo del mismo nivel, y el jugador cruza entre ellas. Arquitectura híbrida C++/Blueprint.",
  },
  {
    id: "arco",
    title: "Arco",
    type: "Plataforma de streaming narrativo",
    status: "Prototipo",
    engine: "Frontend propio",
    desc:
      "Un espacio para publicar ficción por episodios, inspirado en la lectura por capítulos y el cómic vertical, con carga de contenido para creadores.",
  },
];

const MERCH = [
  { id: "poster-sofia", title: "Póster — ¡Investiga con Sofía!", price: "$––", tag: "Edición serie" },
  { id: "pin-mask", title: "Pin esmaltado — máscara TWSTD", price: "$––", tag: "Estudio" },
  { id: "tee-glitch", title: "Playera — logotipo glitch", price: "$––", tag: "Estudio" },
  { id: "sticker-pack", title: "Set de calcomanías", price: "$––", tag: "Estudio" },
];

/* ---------------------------------- UI atoms ---------------------------------- */

function Mark({ size = 28 }) {
  return (
    <svg width={size} height={size * 1.05} viewBox="0 0 40 42" fill="none" aria-hidden="true">
      <path
        d="M20 2c8 0 14 5 14 13 0 12-6 25-14 25S6 27 6 15C6 7 12 2 20 2Z"
        stroke={TOKENS.magenta}
        strokeWidth="1.4"
        transform="translate(1,1)"
      />
      <path
        d="M20 2c8 0 14 5 14 13 0 12-6 25-14 25S6 27 6 15C6 7 12 2 20 2Z"
        stroke={TOKENS.cyan}
        strokeWidth="1.4"
        transform="translate(-1,-1)"
      />
      <path d="M20 2c8 0 14 5 14 13 0 12-6 25-14 25S6 27 6 15C6 7 12 2 20 2Z" fill={TOKENS.ink} />
      <path d="M12 17c2-3 5-3 6 0" stroke={TOKENS.bg} strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <path d="M22 17c2-3 5-3 6 0" stroke={TOKENS.bg} strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <path d="M13 25c3 4 11 4 14 0" stroke={TOKENS.bg} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function Glitch({ children, as: Tag = "span", className = "" }) {
  return (
    <Tag className={`glitch ${className}`} data-text={typeof children === "string" ? children : undefined}>
      {children}
    </Tag>
  );
}

function Eyebrow({ children }) {
  return <div className="eyebrow">{children}</div>;
}

/* --------------------------------- Nav ---------------------------------- */

function Nav({ page, go }) {
  const items = [
    ["home", "INICIO"],
    ["series", "SERIES"],
    ["projects", "PROYECTOS"],
    ["merch", "MERCH"],
  ];
  return (
    <header className="nav">
      <button className="brand" onClick={() => go("home")} aria-label="Ir al inicio">
        <Mark />
        <span className="brand-word">
          TWSTD<span className="brand-word-sub">PRODUCTIONS</span>
        </span>
      </button>
      <nav className="nav-links">
        {items.map(([id, label]) => (
          <button
            key={id}
            className={`nav-link ${page === id || (page === "seriesDetail" && id === "series") ? "is-active" : ""}`}
            onClick={() => go(id)}
          >
            {label}
          </button>
        ))}
      </nav>
    </header>
  );
}

/* --------------------------------- Home ---------------------------------- */

function Home({ go }) {
  return (
    <div className="page">
      <section className="hero">
        <Eyebrow>Casa productora independiente</Eyebrow>
        <h1 className="hero-title">
          <Glitch>TWSTD PRODUCTIONS</Glitch>
        </h1>
        <p className="hero-sub">
          Casa productora de contenido multimedia. Producciones 100% mexicanas. 
        </p>
        <div className="hero-actions">
          <button className="btn btn-primary" onClick={() => go("series")}>
            Ver series
          </button>
          <button className="btn btn-ghost" onClick={() => go("projects")}>
            Ver proyectos
          </button>
        </div>
      </section>

      <section className="strip">
        <div className="strip-head">
          <Eyebrow>Series producidas</Eyebrow>
          <button className="link-more" onClick={() => go("series")}>
            Ver todas →
          </button>
        </div>
        <div className="strip-row">
          {SERIES.map((s) => (
            <SeriesCard key={s.id} s={s} onClick={() => go("seriesDetail", s.id)} compact />
          ))}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------ Series list ------------------------------ */

function SeriesCard({ s, onClick, compact }) {
  return (
    <button className={`card series-card ${compact ? "is-compact" : ""}`} onClick={onClick}>
      <div className="series-card-banner" style={{ background: s.banners[0] }} />
      <div className="series-card-body">
        <Eyebrow>{s.eyebrow}</Eyebrow>
        <h3 className="series-card-title">{s.title}</h3>
        <p className="series-card-tagline">{s.tagline}</p>
      </div>
    </button>
  );
}

function SeriesList({ go }) {
  return (
    <div className="page">
      <section className="section-head">
        <Eyebrow>Catálogo</Eyebrow>
        <h2 className="section-title">Series producidas</h2>
        <p className="section-sub">
          Cada serie tiene su propia página: sinopsis, logotipo y una selección de imágenes que rota sola.
        </p>
      </section>
      <section className="grid">
        {SERIES.map((s) => (
          <SeriesCard key={s.id} s={s} onClick={() => go("seriesDetail", s.id)} />
        ))}
      </section>
    </div>
  );
}

/* ----------------------------- Series detail ----------------------------- */

function SeriesDetail({ id, go }) {
  const s = SERIES.find((x) => x.id === id) || SERIES[0];
  const [bannerIndex, setBannerIndex] = useState(0);
  const reduceMotion = useRef(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    setBannerIndex(0);
    if (reduceMotion.current || s.banners.length < 2) return;
    const t = setInterval(() => {
      setBannerIndex((i) => (i + 1) % s.banners.length);
    }, 5000);
    return () => clearInterval(t);
  }, [s.id, s.banners.length]);

  return (
    <div className="page">
      <button className="back-link" onClick={() => go("series")}>
        ← Series
      </button>

      <section className="banner">
        {s.banners.map((bg, i) => (
          <div
            key={i}
            className="banner-layer"
            style={{ background: bg, opacity: i === bannerIndex ? 1 : 0 }}
          />
        ))}
        <div className="banner-scrim" />
        <div className="banner-content">
          {s.isPlaceholder && <span className="badge">Contenido de ejemplo — reemplázalo</span>}
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h1 className="banner-logo">
            <Glitch>{s.title}</Glitch>
          </h1>
          <p className="banner-meta">{s.meta}</p>
          <p className="banner-synopsis">{s.synopsis}</p>
        </div>
        {s.banners.length > 1 && (
          <div className="banner-dots">
            {s.banners.map((_, i) => (
              <span key={i} className={`dot ${i === bannerIndex ? "is-active" : ""}`} />
            ))}
          </div>
        )}
      </section>

      <section className="gallery">
        <Eyebrow>Imágenes</Eyebrow>
        <div className="gallery-grid">
          {s.banners.map((bg, i) => (
            <div key={i} className="gallery-tile" style={{ background: bg }} />
          ))}
        </div>
      </section>
    </div>
  );
}

/* -------------------------------- Projects -------------------------------- */

function Projects() {
  return (
    <div className="page">
      <section className="section-head">
        <Eyebrow>Catálogo</Eyebrow>
        <h2 className="section-title">Proyectos propios</h2>
        <p className="section-sub">Cosas que TWSTD desarrolla y posee de principio a fin, fuera del formato serie.</p>
      </section>
      <section className="proj-list">
        {PROJECTS.map((p) => (
          <article key={p.id} className="proj-card">
            <div className="proj-card-top">
              <h3 className="proj-title">{p.title}</h3>
              <span className={`status status-${p.status === "En desarrollo" ? "dev" : "proto"}`}>{p.status}</span>
            </div>
            <p className="proj-type">{p.type}</p>
            <p className="proj-desc">{p.desc}</p>
            <p className="proj-engine">{p.engine}</p>
          </article>
        ))}
      </section>
    </div>
  );
}

/* ---------------------------------- Merch ---------------------------------- */

function Merch() {
  return (
    <div className="page">
      <section className="section-head">
        <Eyebrow>Tienda</Eyebrow>
        <h2 className="section-title">Merch</h2>
        <p className="section-sub">
          Objetos del estudio y de cada serie. La tienda todavía no procesa pagos — conecta aquí tu proveedor
          (Shopify, Fourthwall, etc.) cuando estés listo.
        </p>
      </section>
      <section className="merch-grid">
        {MERCH.map((m) => (
          <article key={m.id} className="merch-card">
            <div className="merch-thumb">
              <Mark size={40} />
            </div>
            <div className="merch-info">
              <span className="eyebrow">{m.tag}</span>
              <h3 className="merch-title">{m.title}</h3>
              <div className="merch-row">
                <span className="merch-price">{m.price}</span>
                <span className="merch-soon">Próximamente</span>
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

/* ---------------------------------- Footer ---------------------------------- */

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-row">
        <span>
          TWSTD PRODUCTIONS © {new Date().getFullYear()}
        </span>
        <span className="footer-sub">Series · Proyectos · Merch</span>
      </div>
    </footer>
  );
}

/* ----------------------------------- App ----------------------------------- */

export default function App() {
  const [page, setPage] = useState("home");
  const [seriesId, setSeriesId] = useState(SERIES[0].id);

  function go(next, id) {
    if (id) setSeriesId(id);
    setPage(next);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "auto" });
  }

  let content;
  if (page === "series") content = <SeriesList go={go} />;
  else if (page === "seriesDetail") content = <SeriesDetail id={seriesId} go={go} />;
  else if (page === "projects") content = <Projects />;
  else if (page === "merch") content = <Merch />;
  else content = <Home go={go} />;

  return (
    <div className="app">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Space+Mono:wght@400;700&family=Inter:wght@400;500;600&display=swap');

        * { box-sizing: border-box; }
        .app {
          background: ${TOKENS.bg};
          color: ${TOKENS.ink};
          font-family: 'Inter', system-ui, sans-serif;
          min-height: 100vh;
          width: 100%;
        }
        button { font-family: inherit; cursor: pointer; }

        .eyebrow {
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: ${TOKENS.muted};
          margin-bottom: 8px;
        }

        /* -------- nav -------- */
        .nav {
          position: sticky; top: 0; z-index: 40;
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 28px;
          background: rgba(10,10,12,0.85);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid ${TOKENS.line};
        }
        .brand { display: flex; align-items: center; gap: 10px; background: none; border: none; padding: 0; color: inherit; }
        .brand-word {
          font-family: 'Anton', sans-serif;
          font-size: 18px;
          letter-spacing: 0.04em;
          line-height: 1;
          display: flex; flex-direction: column; align-items: flex-start;
        }
        .brand-word-sub { font-family: 'Space Mono', monospace; font-size: 8px; letter-spacing: 0.2em; color: ${TOKENS.muted}; margin-top: 2px; }
        .nav-links { display: flex; gap: 4px; }
        .nav-link {
          background: none; border: none; color: ${TOKENS.muted};
          font-family: 'Space Mono', monospace; font-size: 11px; letter-spacing: 0.1em;
          padding: 8px 12px; border-radius: 4px; transition: color .2s ease, background .2s ease;
        }
        .nav-link:hover { color: ${TOKENS.ink}; background: ${TOKENS.panel}; }
        .nav-link.is-active { color: ${TOKENS.bg}; background: ${TOKENS.ink}; }
        .nav-link:focus-visible, .brand:focus-visible, .btn:focus-visible, .card:focus-visible, .merch-card:focus-visible {
          outline: 2px solid ${TOKENS.cyan}; outline-offset: 2px;
        }

        /* -------- page fade -------- */
        .page { animation: fadeIn 0.55s ease both; padding: 0 28px 80px; max-width: 1180px; margin: 0 auto; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .page { animation: none; } }

        /* -------- hero -------- */
        .hero { padding: 96px 0 56px; max-width: 760px; }
        .hero-title { font-family: 'Anton', sans-serif; font-size: clamp(40px, 7vw, 76px); line-height: 0.98; margin: 6px 0 20px; letter-spacing: 0.01em; }
        .hero-sub { font-size: 17px; line-height: 1.6; color: ${TOKENS.muted}; max-width: 560px; margin-bottom: 32px; }
        .hero-actions { display: flex; gap: 12px; flex-wrap: wrap; }

        .btn { border-radius: 4px; padding: 12px 22px; font-size: 13px; font-family: 'Space Mono', monospace; letter-spacing: 0.06em; border: 1px solid transparent; }
        .btn-primary { background: ${TOKENS.ink}; color: ${TOKENS.bg}; }
        .btn-primary:hover { background: ${TOKENS.cyan}; }
        .btn-ghost { background: transparent; color: ${TOKENS.ink}; border-color: ${TOKENS.line}; }
        .btn-ghost:hover { border-color: ${TOKENS.ink}; }

        /* -------- glitch text -------- */
        .glitch { position: relative; display: inline-block; }
        .glitch::before, .glitch::after {
          content: attr(data-text); position: absolute; left: 0; top: 0; width: 100%;
          opacity: 0.7; clip-path: inset(0 0 0 0);
        }
        .glitch::before { color: ${TOKENS.cyan}; transform: translate(-2px, 0); animation: glitchA 7s infinite; }
        .glitch::after { color: ${TOKENS.magenta}; transform: translate(2px, 0); animation: glitchB 7s infinite; }
        @keyframes glitchA {
          0%, 94%, 100% { transform: translate(-2px, 0); clip-path: inset(0 0 0 0); }
          95% { transform: translate(-6px, 1px); clip-path: inset(20% 0 30% 0); }
          96% { transform: translate(-1px, -1px); clip-path: inset(60% 0 5% 0); }
        }
        @keyframes glitchB {
          0%, 94%, 100% { transform: translate(2px, 0); clip-path: inset(0 0 0 0); }
          95% { transform: translate(5px, -1px); clip-path: inset(10% 0 55% 0); }
          96% { transform: translate(1px, 1px); clip-path: inset(45% 0 15% 0); }
        }
        @media (prefers-reduced-motion: reduce) { .glitch::before, .glitch::after { animation: none; } }

        /* -------- strip (home) -------- */
        .strip { padding: 24px 0 40px; border-top: 1px solid ${TOKENS.line}; }
        .strip-head { display: flex; align-items: baseline; justify-content: space-between; margin: 24px 0 16px; }
        .link-more { background: none; border: none; color: ${TOKENS.cyan}; font-family: 'Space Mono', monospace; font-size: 12px; }
        .strip-row { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 8px; }
        .strip-row .card.is-compact { min-width: 260px; flex: 0 0 auto; }

        /* -------- section heads -------- */
        .section-head { padding: 64px 0 32px; max-width: 640px; }
        .section-title { font-family: 'Anton', sans-serif; font-size: clamp(30px, 5vw, 44px); margin: 4px 0 12px; }
        .section-sub { color: ${TOKENS.muted}; font-size: 15px; line-height: 1.6; }

        /* -------- series cards -------- */
        .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 18px; }
        .card {
          text-align: left; background: ${TOKENS.panel}; border: 1px solid ${TOKENS.line}; border-radius: 8px;
          padding: 0; color: inherit; overflow: hidden; transition: border-color .2s ease, transform .2s ease;
        }
        .card:hover { border-color: ${TOKENS.cyan}; transform: translateY(-2px); }
        .series-card-banner { height: 140px; }
        .series-card-body { padding: 16px 18px 20px; }
        .series-card-title { font-family: 'Anton', sans-serif; font-size: 20px; margin: 0 0 8px; letter-spacing: 0.01em; }
        .series-card-tagline { color: ${TOKENS.muted}; font-size: 13.5px; line-height: 1.5; margin: 0; }

        .back-link { background: none; border: none; color: ${TOKENS.muted}; font-family: 'Space Mono', monospace; font-size: 12px; margin: 24px 0 16px; }
        .back-link:hover { color: ${TOKENS.ink}; }

        /* -------- series banner (detail) -------- */
        .banner { position: relative; min-height: 460px; border-radius: 10px; overflow: hidden; display: flex; align-items: flex-end; }
        .banner-layer { position: absolute; inset: 0; transition: opacity 1.2s ease; }
        .banner-scrim { position: absolute; inset: 0; background: linear-gradient(0deg, rgba(10,10,12,0.96) 0%, rgba(10,10,12,0.55) 45%, rgba(10,10,12,0.15) 100%); }
        .banner-content { position: relative; padding: 40px; max-width: 720px; }
        .badge {
          display: inline-block; font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em;
          color: ${TOKENS.bg}; background: ${TOKENS.cyan}; padding: 4px 8px; border-radius: 3px; margin-bottom: 12px;
        }
        .banner-logo { font-family: 'Anton', sans-serif; font-size: clamp(30px, 5vw, 52px); line-height: 1; margin: 4px 0 10px; }
        .banner-meta { font-family: 'Space Mono', monospace; font-size: 12px; color: ${TOKENS.muted}; margin: 0 0 14px; }
        .banner-synopsis { font-size: 15.5px; line-height: 1.65; max-width: 620px; color: ${TOKENS.ink}; opacity: 0.92; }
        .banner-dots { position: absolute; right: 20px; bottom: 20px; display: flex; gap: 6px; }
        .dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(242,240,234,0.35); }
        .dot.is-active { background: ${TOKENS.cyan}; }

        .gallery { padding: 40px 0; }
        .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin-top: 12px; }
        .gallery-tile { height: 140px; border-radius: 6px; border: 1px solid ${TOKENS.line}; }

        /* -------- projects -------- */
        .proj-list { display: flex; flex-direction: column; gap: 14px; }
        .proj-card { background: ${TOKENS.panel}; border: 1px solid ${TOKENS.line}; border-radius: 8px; padding: 22px 24px; }
        .proj-card-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .proj-title { font-family: 'Anton', sans-serif; font-size: 22px; margin: 0; }
        .status { font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em; padding: 4px 9px; border-radius: 3px; text-transform: uppercase; }
        .status-dev { background: rgba(47,224,230,0.15); color: ${TOKENS.cyan}; }
        .status-proto { background: rgba(255,59,110,0.15); color: ${TOKENS.magenta}; }
        .proj-type { color: ${TOKENS.muted}; font-size: 13px; margin: 8px 0 10px; }
        .proj-desc { font-size: 14.5px; line-height: 1.6; margin: 0 0 10px; }
        .proj-engine { font-family: 'Space Mono', monospace; font-size: 11px; color: ${TOKENS.muted}; margin: 0; }

        /* -------- merch -------- */
        .merch-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
        .merch-card { background: ${TOKENS.panel}; border: 1px solid ${TOKENS.line}; border-radius: 8px; overflow: hidden; }
        .merch-thumb { height: 140px; display: flex; align-items: center; justify-content: center; background: ${TOKENS.panel2}; }
        .merch-info { padding: 14px 16px 18px; }
        .merch-title { font-size: 15px; font-weight: 600; margin: 4px 0 10px; }
        .merch-row { display: flex; align-items: center; justify-content: space-between; }
        .merch-price { font-family: 'Space Mono', monospace; font-size: 13px; color: ${TOKENS.muted}; }
        .merch-soon { font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.06em; color: ${TOKENS.magenta}; text-transform: uppercase; }

        /* -------- footer -------- */
        .footer { border-top: 1px solid ${TOKENS.line}; padding: 24px 28px; margin-top: 40px; }
        .footer-row { max-width: 1180px; margin: 0 auto; display: flex; justify-content: space-between; font-family: 'Space Mono', monospace; font-size: 11px; color: ${TOKENS.muted}; flex-wrap: wrap; gap: 8px; }

        @media (max-width: 640px) {
          .nav { padding: 14px 16px; }
          .page { padding: 0 16px 60px; }
          .hero { padding: 64px 0 40px; }
          .banner-content { padding: 24px; }
          .nav-links { gap: 0; }
          .nav-link { padding: 8px 8px; font-size: 10px; }
        }
      `}</style>

      <Nav page={page} go={go} />
      {content}
      <Footer />
    </div>
  );
}
